import { useSyncExternalStore } from 'react';

import { Button } from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { OfflineArt } from '@/components/illustrations';

function subscribe(onChange: () => void) {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

/**
 * Covers the app while the browser reports no network, and lifts itself when it comes back.
 * It sits OVER the app rather than replacing it, so nothing unmounts and a half-written
 * self-assessment is still there afterwards. Only `onLine === false` is trusted — `true` just
 * means an adapter is up. The fade waits half a second so a network handover doesn't flash it.
 * Reloading while offline never reaches this component; public/sw.js serves offline.html then.
 */
export function OfflineOverlay() {
  const online = useSyncExternalStore(subscribe, () => navigator.onLine);
  if (online) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label="You are offline"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background animate-in fade-in delay-500 fill-mode-backwards"
    >
      <EmptyState art={OfflineArt} title="You are offline" body="Check your internet connection. This page comes back on its own once you're online.">
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try again
        </Button>
      </EmptyState>
    </div>
  );
}
